import { useEffect, useRef, useState } from 'react'
import { injectIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { ModalHeader } from '@vibialighting/ui-kit'
import _ from 'lodash'

import Config from '@/config'
import { useCatalog } from "@/hooks"
import { getURL } from '@/utils'
import { pushSignupComplete } from '@/utils/signupDataLayer'
import { authActions, errorActions, supportActions, uiActions } from '@/redux/actions'
import { AUTH_VIEWS } from '@/constants'
import userStatus from '@/constants/userStatus.constants'
import { useModalContext } from '@/contexts/ModalContext'
import { LANGUAGE_LOCALES_CODE } from '@/constants'
import Toggles from '@domain/toggles/Toggles'
import './ModalAuth.new.scss'

const IconClose = (p) => (
	<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" {...p}>
		<line x1="6" y1="6" x2="18" y2="18" />
		<line x1="18" y1="6" x2="6" y2="18" />
	</svg>
)

const ModalAuth = (props) => {
	const {
		intl: { formatMessage, locale },
		authView = 'login',
		authViewParams = {},
		language,
		onAuth,
		onClose,
		authViewState = {}
	} = props

	const { modalUnmount } = useModalContext()
	const dispatch = useDispatch()
	const location = useLocation()
	const { isUsaCatalog } = useCatalog()
	const [title, setTitle] = useState('')
	const [activeView, setActiveView] = useState(null)
	const [iframeScrolled, setIframeScrolled] = useState(false)

	const iframeRef = useRef(null)
	const [fullScreenModal, setFullScreenModal] = useState(window.innerWidth < 575)

	const frAuthPath = Config.getInstance().getConfig('endpoints.fr-auth.url')
	const frAuthSubdomain = Config.getInstance().getConfig('endpoints.fr-auth.subdomain')
	const frAuthUrl = getURL(frAuthPath, frAuthSubdomain)
	const allowedLocaleValues = Object.values(LANGUAGE_LOCALES_CODE)
	const defaultModalLanguage = LANGUAGE_LOCALES_CODE[locale] || LANGUAGE_LOCALES_CODE.en
	const sanitizedModalLanguage = allowedLocaleValues.includes(authViewParams.language)
		? authViewParams.language
		: defaultModalLanguage

	const paramsString = Object.entries(authViewParams).reduce((string, [key, value]) => {
		if (value === undefined || key === 'language') {
			return string
		}
		if (key === 'supportTypes') {
			const arr = Array.isArray(value) ? value : [value]
			return `${string}&${key}=${encodeURIComponent(JSON.stringify(arr))}`
		}
		return `${string}&${key}=${value}`
	}, `language=${sanitizedModalLanguage}`)

	// viewUrl se fija al montar para evitar que cambios posteriores en `locale`
	// (via SET_LOCALE tras login) recomputen `paramsString` y muten `iframe.src`,
	// lo que recargaría el iframe entero llevándolo de vuelta a root URL (login).
	// El iframe gestiona su propia navegación interna vía React Router.
	const [viewUrl] = useState(
		() => frAuthUrl + "/" + (AUTH_VIEWS[authView] || '') + (paramsString ? `?${paramsString}` : '')
	)

	const handleClose = () => {
		window.parent?.postMessage({ event: 'authenticateFailed', error: 'Modal Closed' }, '*')
		let forceRerender = false
		typeof onClose === 'function' && onClose()
		dispatch(authActions.showModalAuth(false))
		if (activeView == 'support' && authViewParams.siteLocation == 'TSSAlert') {
			forceRerender = true
		}
		modalUnmount(forceRerender)
		switch (activeView) {
			case 'login':
				dispatch(authActions.modalAuthClosed())
				break
			case 'signUp':
				dispatch(authActions.signUpSocialError())
				break
			case 'qualification':
				dispatch(authActions.qualifiedError({ message: 'modal closed' }))
				break
			case 'support':
				dispatch(supportActions.supportRequestedFailure({ message: 'modal closed' }))
				break
		}
	}

	const handleAuth = async (e) => {
		if (e.origin !== frAuthUrl) {
			return
		}

		if (e.data.id === 'vibia.auth.signUp') {
			dispatch(authActions.signUpSuccess(e.data.payload))
			// DV-6394: país/perfil van en crudo (geonameId / id de profesión);
			// formato final pendiente de confirmar con Marketing.
			pushSignupComplete({
				email: e.data.payload?.email,
				userId: e.data.payload?.userId,
				country: e.data.payload?.ubigeo?.country?.geonameId,
				profession: e.data.payload?.profession
			})
		}

		if (e.data.id === 'login.social.success.credentials') {
			dispatch(authActions.signUpSocialSuccess(e.data.payload))
		}

		if (e.data === 'vibia.auth.logged') {
			typeof onAuth === 'function' && onAuth()
		}

		if (e.data.id === 'vibia.auth.qualified.success') {
			try {
				await dispatch(authActions.getAccountDetails())
				dispatch(authActions.qualifiedSuccess())
			} catch (e) {
				dispatch(errorActions.setError('Something wrong happened'))
			}
		}

		if (e.data.id === 'vibia.auth.qualified.error') {
			dispatch(authActions.qualifiedError(e.data.payload))
		}

		if (e.data.id === 'vibia.supportRequested.success') {
			dispatch(supportActions.supportRequestedSuccess(e.data.payload))
		}

		if (e.data.id === 'vibia.supportRequested.error') {
			dispatch(supportActions.supportRequestedFailure(e.data.payload))
		}

		if (e.data === 'vibia.auth.close') {
			dispatch(authActions.showModalAuth(false))
			modalUnmount()
		}

		if (e.data === 'vibia.auth.loginTimeout') {
			dispatch(authActions.loginTimeout())
		}

		if (e.data === 'vibia.auth.ready') {
			handleFullScreenMessage(fullScreenModal)
			handleStateMessage()
		}

		if (/^vibia.jwtToken./.test(e.data)) {
			const token = e.data.substring(15)
			dispatch(authActions.saveAuthToken(token))
		}

		if (/^vibia.project_budget./.test(e.data)) {
			const { projectId, siteLocation } = JSON.parse(e.data.substring(21))

			await dispatch(authActions.getAccountDetails())

			dispatch(
				uiActions.navigateTo({
					path: isUsaCatalog()
						? `/project/${projectId}/usa-request-budget`
						: `/project/${projectId}/request-budget?continue=${location.pathname}`,
					requiredUserStatus: userStatus.QUALIFIED,
					state: {
						siteLocation
					}
				})
			)
			dispatch(authActions.showModalAuth(false))
			modalUnmount()
		}

		if (e.data.id === 'vibia.support.form.error') {
			dispatch(supportActions.supportRequestedFailure(e.data.payload))
		}

		if (e.data.id === 'login.message.credentials') {
			dispatch(authActions.supportCredentialsError(e.data.payload))
		}

		if (e.data.id === 'login.social.error.credentials') {
			dispatch(authActions.signUpSocialError(e.data.payload))
		}

	}

	const handleMessage = (e) => {
		if (e.origin !== frAuthUrl) {
			return
		}
		switch (e.data?.type) {
			case 'VIEW_LOADED':
				setTitle(`modal.${e.data.payload}.title`)
				setActiveView(e.data.payload)
				break
			case 'AUTH_SCROLL':
				setIframeScrolled(Boolean(e.data.scrolled))
				break
		}
	}

	useEffect(() => {
		window.addEventListener('message', handleAuth)
		return () => {
			window.removeEventListener('message', handleAuth)
		}
	}, [])

	useEffect(() => {
		window.addEventListener('message', handleMessage)
		return () => {
			window.removeEventListener('message', handleMessage)
		}
	}, [])

	useEffect(() => {
		const mqlFullScreenModal = window.matchMedia('(max-width: 575px)')
		mqlFullScreenModal.addListener(handleFullScreenModal)
		return () => mqlFullScreenModal.removeListener(handleFullScreenModal)
	}, [])

	useEffect(() => {
		handleFullScreenMessage(fullScreenModal)
	}, [fullScreenModal])

	useEffect(() => {
		handleStateMessage()
	}, [authViewState])

	const handleFullScreenModal = (mdlEvent) => {
		setFullScreenModal(mdlEvent.matches)
	}

	const handleFullScreenMessage = (fullScreenModal) => {
		if (Toggles.isEnabled('newAuthForm')) {
			iframeRef?.current &&
				iframeRef.current.contentWindow.postMessage('vibia.screen.fullScreenModal', '*')
			return
		}
		iframeRef?.current &&
			iframeRef.current.contentWindow.postMessage(
				`vibia.screen.${fullScreenModal ? 'fullScreenModal' : 'defaultModal'}`,
				'*'
			)
	}

	const handleStateMessage = () => {
		if (!_.isEmpty(authViewState)) {
			setTimeout(() => {
				iframeRef?.current &&
					iframeRef.current.contentWindow.postMessage(`vibia.support.state=${JSON.stringify(authViewState)}`, '*')
				iframeRef?.current &&
					iframeRef.current.contentWindow.postMessage({ type: 'SUPPORT_STATE', payload: authViewState }, '*')
			})
		}
	}

	// When qualification is mandatory (or the NewRegister flow, which the legacy
	// modal already blocked via `onClose={() => {}}`), the user must not be able
	// to dismiss the modal — the X is hidden so the qualification can't be skipped.
	const blockClose =
		authViewParams.siteLocation === 'NewRegister' || Boolean(authViewParams.mandatoryQualification)

	if (Toggles.isEnabled('newAuthForm')) {
		const supportClass = authView === 'support' ? ' vib-modal-auth-new--support' : ''
		return (
			<div className={`vib-modal-auth-new${supportClass}${iframeScrolled ? ' vib-modal-auth-new--scrolled' : ''}`}>
				{!blockClose && (
					<button
						type="button"
						className="vib-modal-auth-new__close"
						onClick={handleClose}
						aria-label={formatMessage({ id: 'auth.close', defaultMessage: 'Cerrar' })}
						data-qa="modalAuthHeader"
					>
						<IconClose />
					</button>
				)}
				<iframe
					src={viewUrl}
					frameBorder="0"
					name="authIframe"
					ref={iframeRef}
					className="vib-modal-auth-new__iframe"
					title={formatMessage({ id: 'auth.iframe.title', defaultMessage: 'Acceso profesional' })}
				/>
			</div>
		)
	}

	return (
		<>
			{authViewParams.siteLocation === 'NewRegister' ? (
				<ModalHeader onClose={() => { }} style={{ paddingLeft: '2rem' }}>
					<img src="/vibia-logo.png" alt="Vibia" />
				</ModalHeader>
			) : (
				<ModalHeader onClose={handleClose} data-qa="modalAuthHeader">
					{title ? formatMessage({ id: title }) : formatMessage({ id: 'title.loading' })}
				</ModalHeader>
			)}
			<iframe
				src={viewUrl}
				frameBorder="0"
				name="authIframe"
				ref={iframeRef}
				style={{
					border: '0px solid white',
					height: '79vh',
					width: '100%'
				}}
			/>
		</>
	)
}

export default injectIntl(ModalAuth)
